import React from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import { pipe, Option } from 'effect';

import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@/components/mui/Tooltip';
import InfoLabel from '@/components/mui/InfoLabel';

import type { BankFeedTransactionStatus } from '@/domain/bankFeed/schemas';

type ChipColor = 'success' | 'warning' | 'default';
type StatusStyle = { color: ChipColor; Icon: React.ComponentType<{ fontSize?: 'small' }> };

// Backend owns the localised `label`; we key chip colour + icon off the raw `id`
// (case-insensitive). Unknown / future statuses fall back to a neutral chip with an
// info icon, so the column stays styled even as the BE status vocabulary grows.
const STATUS_STYLES: Record<string, StatusStyle> = {
  booked: { color: 'success', Icon: DoneAllRoundedIcon },
  pending: { color: 'warning', Icon: ScheduleRoundedIcon },
};
const DEFAULT_STYLE: StatusStyle = { color: 'default', Icon: InfoOutlinedIcon };

type StatusCellProps = ICellRendererParams<unknown, BankFeedTransactionStatus | null | undefined>;

const StatusCell: React.FC<StatusCellProps> = ({ value }) => {
  if (!value) {
    return null;
  }

  const { color, Icon } = pipe(
    Option.fromNullable(STATUS_STYLES[value.id.toLowerCase()]),
    Option.getOrElse(() => DEFAULT_STYLE),
  );

  return (
    <Stack direction="row" alignItems="center" height="100%">
      <Tooltip t={value.hint} placement="top">
        <Chip
          icon={<Icon fontSize="small" />}
          label={value.hint ? <InfoLabel>{value.label}</InfoLabel> : value.label}
          color={color}
          size="small"
          sx={{ fontWeight: 400 }}
        />
      </Tooltip>
    </Stack>
  );
};

export default StatusCell;
